import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { api } from '../services/api';

// Hardcoded class type IDs (these don't change)
const CLASS_TYPE_IDS = {
  MANDARIN_GROUP: 'mandarin',
  CANTONESE_GROUP: 'cantonese',
  MANDARIN_TUTORING: 'mandarin-tutoring-trial',
  CANTONESE_TUTORING: 'cantonese-tutoring-trial'
};

// Default pricing (fallback if API fails)
export const DEFAULT_PRICING = {
  private: {
    trial: { price: 450, hours: 1, label: 'Trial', perHour: 450 },
    '10hr': { price: 5500, hours: 10, label: '10 hours', perHour: 550 },
    '20hr': { price: 10000, hours: 20, label: '20 hours', perHour: 500, discount: '9%' },
    '30hr': { price: 13500, hours: 30, label: '30 hours', perHour: 450, discount: '18%' },
  },
  group: {
    starter: { price: 3000, classes: 10, label: 'Starter', perClass: 300 },
  },
};

// Format price with commas (e.g., 12000 -> "12,000")
export const formatPrice = (price) => {
  return price.toLocaleString();
};

// Cache fetched pricing in localStorage to avoid showing a loading state on return visits
const PRICING_CACHE_KEY = 'pricingCacheV2';
const PRICING_CACHE_TTL = 60 * 60 * 1000; // 1 hour

const getCachedPricing = () => {
  try {
    const cached = localStorage.getItem(PRICING_CACHE_KEY);
    if (cached) {
      const { pricing, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < PRICING_CACHE_TTL) {
        return pricing;
      }
    }
  } catch (e) {}
  return null;
};

const savePricingToCache = (pricing) => {
  try {
    localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify({ pricing, timestamp: Date.now() }));
  } catch (e) {}
};

/**
 * Root page for language selection - asks users to choose between Mandarin and Cantonese
 */
const LanguageSelectionPage = () => {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedPrivateMandarin, setSelectedPrivateMandarin] = useState('trial');
  const [selectedPrivateCantonese, setSelectedPrivateCantonese] = useState('trial');
  const [selectedGroupMandarin, setSelectedGroupMandarin] = useState('starter');
  const [selectedGroupCantonese, setSelectedGroupCantonese] = useState('starter');
  
  // Mobile tab state - Mandarin first
  const [activeLanguageTab, setActiveLanguageTab] = useState('mandarin');

  // Pricing state - seed from cache so return visitors never see a loading state
  const [pricing, setPricing] = useState(() => getCachedPricing() || DEFAULT_PRICING);
  const [pricingLoaded, setPricingLoaded] = useState(() => !!getCachedPricing());

  // Load pricing on mount
  useEffect(() => {
    const loadPricing = async () => {
      try {
        const settings = await api.admin.getSettings();
        if (settings?.pricing) {
          const parsed = typeof settings.pricing === 'string'
            ? JSON.parse(settings.pricing)
            : settings.pricing;
          // Deep merge with defaults to ensure all fields exist
          const mergedPricing = {
            private: {
              trial: { ...DEFAULT_PRICING.private.trial, ...(parsed.private?.trial || {}) },
              '10hr': { ...DEFAULT_PRICING.private['10hr'], ...(parsed.private?.['10hr'] || {}) },
              '20hr': { ...DEFAULT_PRICING.private['20hr'], ...(parsed.private?.['20hr'] || {}) },
              '30hr': { ...DEFAULT_PRICING.private['30hr'], ...(parsed.private?.['30hr'] || {}) },
            },
            group: {
              starter: { ...DEFAULT_PRICING.group.starter, ...(parsed.group?.starter || {}) },
            },
          };
          setPricing(mergedPricing);
          savePricingToCache(mergedPricing);
        }
      } catch (err) {
        console.error('Error loading pricing, using defaults:', err);
        // Use default pricing on error
      } finally {
        setPricingLoaded(true);
      }
    };

    loadPricing();
  }, []);

  // Handle language selection for group classes
  const handleGroupClassSelect = (classTypeId) => {
    window.location.href = `/start/${classTypeId}`;
  };

  // Handle private tutor booking
  const handlePrivateTutorSelect = (language) => {
    const url = `https://book.mandarintutorhk.com/start/${language}`;
    window.location.href = url;
  };

  // Toggle card expansion (for desktop)
  const toggleCard = (cardName) => {
    setExpandedCard(expandedCard === cardName ? null : cardName);
  };

  // Render pricing option (radio button)
  const renderPricingOption = (name, value, selected, onChange, label, sublabel, price, priceSubtext) => {
    const isSelected = selected === value;
    return (
      <label
        className={`flex justify-between items-center py-2 px-3 rounded border-2 cursor-pointer transition-colors ${
          isSelected
            ? 'bg-purple-600 bg-opacity-30 border-purple-500'
            : 'bg-gray-800 bg-opacity-50 border-gray-700 hover:bg-purple-600 hover:bg-opacity-20 hover:border-purple-600'
        }`}
      >
        <input
          type="radio"
          name={name}
          value={value}
          checked={isSelected}
          onChange={() => onChange(value)}
          className="sr-only"
        />
        <div className="flex items-center gap-3 flex-1">
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              isSelected ? 'border-purple-400' : 'border-gray-600'
            }`}
          >
            <div
              className={`w-2.5 h-2.5 rounded-full ${isSelected ? 'bg-purple-400' : 'bg-transparent'}`}
            ></div>
          </div>
          <div>
            <div className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-300'}`}>{label}</div>
            {pricingLoaded && <div className={`text-xs ${isSelected ? 'text-gray-300' : 'text-gray-400'}`}>{sublabel}</div>}
          </div>
        </div>
        {!pricingLoaded ? (
          <div className="text-right space-y-1">
            <div className="animate-pulse bg-gray-600 rounded h-5 w-20"></div>
            {priceSubtext && <div className="animate-pulse bg-gray-700 rounded h-3 w-14 ml-auto"></div>}
          </div>
        ) : priceSubtext ? (
          <div className="text-right">
            <div className="text-white font-bold text-lg">{price}</div>
            <div className="text-xs text-purple-300">{priceSubtext}</div>
          </div>
        ) : (
          <span className={`font-semibold ${isSelected ? 'text-white' : 'text-white'}`}>{price}</span>
        )}
      </label>
    );
  };

  // Render language card (for desktop)
  const renderLanguageCard = (language, isExpanded, privateSelected, setPrivateSelected, groupSelected, setGroupSelected) => {
    const isMandarin = language === 'mandarin';
    const displayName = isMandarin ? 'MANDARIN' : 'CANTONESE';
    const groupClassTypeId = isMandarin ? CLASS_TYPE_IDS.MANDARIN_GROUP : CLASS_TYPE_IDS.CANTONESE_GROUP;
    const privateTutoringId = isMandarin ? CLASS_TYPE_IDS.MANDARIN_TUTORING : CLASS_TYPE_IDS.CANTONESE_TUTORING;

    const p = pricing.private;
    const g = pricing.group;

    return (
      <div className="bg-gray-800 bg-opacity-50 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
        {/* Card Header - Always Visible, Clickable on Mobile */}
        <button
          className="w-full p-8 md:pointer-events-none flex items-center justify-between md:justify-center"
          onClick={() => toggleCard(language)}
        >
          <h2 className="text-2xl font-bold text-center">
            <span className="bg-gradient-to-r from-purple-400 via-white to-purple-400 bg-clip-text text-transparent animate-shimmer">
              {displayName}
            </span>
          </h2>
          <svg
            className={`w-6 h-6 text-purple-400 md:hidden transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Card Content - Collapsible on Mobile */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden px-8 pb-8 md:!max-h-none md:!opacity-100 ${
            isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          {/* Private Tutor Section - NOW FIRST */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-purple-300 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Private Tutor
            </h3>

            {/* Private Tutor Pricing Table */}
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 mb-4">
              <div className="space-y-2 text-sm">
                {renderPricingOption(
                  `${language}-private`,
                  'trial',
                  privateSelected,
                  setPrivateSelected,
                  p.trial.label,
                  `${p.trial.hours} hour`,
                  `HKD ${formatPrice(p.trial.price)}`
                )}
                {renderPricingOption(
                  `${language}-private`,
                  '10hr',
                  privateSelected,
                  setPrivateSelected,
                  p['10hr'].label,
                  `HKD ${formatPrice(p['10hr'].perHour)}/hr`,
                  `HKD ${formatPrice(p['10hr'].price)}`
                )}
                {renderPricingOption(
                  `${language}-private`,
                  '20hr',
                  privateSelected,
                  setPrivateSelected,
                  p['20hr'].label,
                  `HKD ${formatPrice(p['20hr'].perHour)}/hr${p['20hr'].discount ? ` • Save ${p['20hr'].discount}` : ''}`,
                  `HKD ${formatPrice(p['20hr'].price)}`
                )}
                {renderPricingOption(
                  `${language}-private`,
                  '30hr',
                  privateSelected,
                  setPrivateSelected,
                  p['30hr'].label,
                  `HKD ${formatPrice(p['30hr'].perHour)}/hr${p['30hr'].discount ? ` • Save ${p['30hr'].discount}` : ''}`,
                  `HKD ${formatPrice(p['30hr'].price)}`
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-gray-700 text-xs text-gray-400 space-y-1">
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Flexible scheduling
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Customized curriculum
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Travel fee from HKD 50 if outside Central
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  24-hour cancellation required
                </div>
              </div>
            </div>

            <button
              onClick={() => handlePrivateTutorSelect(privateTutoringId)}
              className="bg-purple-600 hover:bg-purple-700 w-full transition-colors rounded-lg py-3 px-4 font-semibold shadow-lg shadow-purple-900/50"
            >
              Book a Private Tutor →
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gray-800 px-2 text-gray-500">or</span>
            </div>
          </div>

          {/* Group Classes Section - NOW SECOND */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-purple-300 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              Group Classes
            </h3>

            {/* Group Class Pricing Table */}
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 mb-4">
              <div className="space-y-2 text-sm">
                {renderPricingOption(
                  `${language}-group`,
                  'starter',
                  groupSelected,
                  setGroupSelected,
                  '10 one-hour classes',
                  `HKD ${formatPrice(g.starter.perClass)}/class`,
                  `HKD ${formatPrice(g.starter.price)}`,
                  null
                )}
              </div>

              {/* Group Class Features */}
              <div className="mt-3 pt-3 border-t border-gray-700 text-xs text-gray-400 space-y-1">
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Small class sizes (max 4 students)
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Practice conversation with peers
                </div>
              </div>
            </div>

            <button
              onClick={() => handleGroupClassSelect(groupClassTypeId)}
              className="bg-purple-600 hover:bg-purple-700 w-full transition-colors rounded-lg py-3 px-4 font-semibold shadow-lg shadow-purple-900/50"
            >
              Join a Group Class →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render mobile tabbed content
  const renderMobileTabbedContent = () => {
    const language = activeLanguageTab;
    const isMandarin = language === 'mandarin';
    const groupClassTypeId = isMandarin ? CLASS_TYPE_IDS.MANDARIN_GROUP : CLASS_TYPE_IDS.CANTONESE_GROUP;
    const privateTutoringId = isMandarin ? CLASS_TYPE_IDS.MANDARIN_TUTORING : CLASS_TYPE_IDS.CANTONESE_TUTORING;
    const privateSelected = isMandarin ? selectedPrivateMandarin : selectedPrivateCantonese;
    const setPrivateSelected = isMandarin ? setSelectedPrivateMandarin : setSelectedPrivateCantonese;
    const groupSelected = isMandarin ? selectedGroupMandarin : selectedGroupCantonese;
    const setGroupSelected = isMandarin ? setSelectedGroupMandarin : setSelectedGroupCantonese;

    const p = pricing.private;
    const g = pricing.group;

    return (
      <div className="md:hidden">
        {/* Mobile Language Tabs */}
        <div className="mb-4">
          <div className="flex bg-gray-900 rounded-lg p-1">
            <button
              onClick={() => setActiveLanguageTab('mandarin')}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all ${
                activeLanguageTab === 'mandarin'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Mandarin
            </button>
            <button
              onClick={() => setActiveLanguageTab('cantonese')}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all ${
                activeLanguageTab === 'cantonese'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Cantonese
            </button>
          </div>
        </div>

        {/* Mobile Tab Content */}
        <div>
          {/* Private Tutor Section */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-purple-300 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Private Tutor
            </h3>

            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 mb-3">
              <div className="space-y-2">
                {/* Trial */}
                <label className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  privateSelected === 'trial'
                    ? 'bg-purple-600/30 border-2 border-purple-500'
                    : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                }`}>
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name={`${language}-private-mobile`}
                      checked={privateSelected === 'trial'}
                      onChange={() => setPrivateSelected('trial')}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className={`font-semibold text-sm ${privateSelected === 'trial' ? 'text-white' : 'text-gray-300'}`}>
                        {p.trial.label}
                      </div>
                      {pricingLoaded && <div className={`text-xs ${privateSelected === 'trial' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {p.trial.hours} hour
                      </div>}
                    </div>
                  </div>
                  <div className="text-right">
                    {!pricingLoaded ? (
                      <div className="animate-pulse bg-gray-600 rounded h-5 w-20"></div>
                    ) : (
                      <div className={`font-bold ${privateSelected === 'trial' ? 'text-white' : 'text-gray-300'}`}>
                        HKD {formatPrice(p.trial.price)}
                      </div>
                    )}
                  </div>
                </label>

                {/* 10 hours */}
                <label className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  privateSelected === '10hr'
                    ? 'bg-purple-600/30 border-2 border-purple-500'
                    : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                }`}>
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name={`${language}-private-mobile`}
                      checked={privateSelected === '10hr'}
                      onChange={() => setPrivateSelected('10hr')}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className={`font-semibold text-sm ${privateSelected === '10hr' ? 'text-white' : 'text-gray-300'}`}>
                        {p['10hr'].label}
                      </div>
                      {pricingLoaded && <div className={`text-xs ${privateSelected === '10hr' ? 'text-gray-400' : 'text-gray-500'}`}>
                        HKD {formatPrice(p['10hr'].perHour)}/hr
                      </div>}
                    </div>
                  </div>
                  <div className="text-right">
                    {!pricingLoaded ? (
                      <div className="animate-pulse bg-gray-600 rounded h-5 w-20"></div>
                    ) : (
                      <div className={`font-bold ${privateSelected === '10hr' ? 'text-white' : 'text-gray-300'}`}>
                        HKD {formatPrice(p['10hr'].price)}
                      </div>
                    )}
                  </div>
                </label>

                {/* 20 hours */}
                <label className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  privateSelected === '20hr'
                    ? 'bg-purple-600/30 border-2 border-purple-500'
                    : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                }`}>
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name={`${language}-private-mobile`}
                      checked={privateSelected === '20hr'}
                      onChange={() => setPrivateSelected('20hr')}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className={`font-semibold text-sm ${privateSelected === '20hr' ? 'text-white' : 'text-gray-300'}`}>
                        {p['20hr'].label}
                      </div>
                      {pricingLoaded && <div className={`text-xs ${privateSelected === '20hr' ? 'text-purple-400' : 'text-purple-400'}`}>
                        HKD {formatPrice(p['20hr'].perHour)}/hr{p['20hr'].discount ? ` • Save ${p['20hr'].discount}` : ''}
                      </div>}
                    </div>
                  </div>
                  <div className="text-right">
                    {!pricingLoaded ? (
                      <div className="animate-pulse bg-gray-600 rounded h-5 w-20"></div>
                    ) : (
                      <div className={`font-bold ${privateSelected === '20hr' ? 'text-white' : 'text-gray-300'}`}>
                        HKD {formatPrice(p['20hr'].price)}
                      </div>
                    )}
                  </div>
                </label>

                {/* 30 hours */}
                <label className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  privateSelected === '30hr'
                    ? 'bg-purple-600/30 border-2 border-purple-500'
                    : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                }`}>
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name={`${language}-private-mobile`}
                      checked={privateSelected === '30hr'}
                      onChange={() => setPrivateSelected('30hr')}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className={`font-semibold text-sm ${privateSelected === '30hr' ? 'text-white' : 'text-gray-300'}`}>
                        {p['30hr'].label}
                      </div>
                      {pricingLoaded && <div className={`text-xs ${privateSelected === '30hr' ? 'text-purple-400' : 'text-purple-400'}`}>
                        HKD {formatPrice(p['30hr'].perHour)}/hr{p['30hr'].discount ? ` • Save ${p['30hr'].discount}` : ''}
                      </div>}
                    </div>
                  </div>
                  <div className="text-right">
                    {!pricingLoaded ? (
                      <div className="animate-pulse bg-gray-600 rounded h-5 w-20"></div>
                    ) : (
                      <div className={`font-bold ${privateSelected === '30hr' ? 'text-white' : 'text-gray-300'}`}>
                        HKD {formatPrice(p['30hr'].price)}
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* All 4 Features */}
              <div className="mt-3 pt-3 border-t border-gray-800 text-xs text-gray-400 space-y-1">
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Flexible scheduling
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Customized curriculum
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Travel fee from HKD 50 if outside Central
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  24-hour cancellation required
                </div>
              </div>
            </div>

            <button
              onClick={() => handlePrivateTutorSelect(privateTutoringId)}
              className="bg-purple-600 hover:bg-purple-700 w-full transition-colors rounded-lg py-3 px-4 font-semibold shadow-lg shadow-purple-900/50"
            >
              Book a Private Tutor →
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-black px-2 text-gray-500">or</span>
            </div>
          </div>

          {/* Group Classes Section */}
          <div>
            <h3 className="text-lg font-bold text-purple-300 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              Group Classes
            </h3>

            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 mb-3">
              <div className="space-y-2">
                {/* Starter */}
                <label className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  groupSelected === 'starter'
                    ? 'bg-purple-600/30 border-2 border-purple-500'
                    : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                }`}>
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name={`${language}-group-mobile`}
                      checked={groupSelected === 'starter'}
                      onChange={() => setGroupSelected('starter')}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className={`font-semibold text-sm ${groupSelected === 'starter' ? 'text-white' : 'text-gray-300'}`}>
                        10 one-hour classes
                      </div>
                      {pricingLoaded && <div className={`text-xs ${groupSelected === 'starter' ? 'text-gray-400' : 'text-gray-500'}`}>
                        HKD {formatPrice(g.starter.perClass)}/class
                      </div>}
                    </div>
                  </div>
                  <div className="text-right">
                    {!pricingLoaded ? (
                      <div className="space-y-1">
                        <div className="animate-pulse bg-gray-600 rounded h-5 w-20"></div>
                        <div className="animate-pulse bg-gray-700 rounded h-3 w-14 ml-auto"></div>
                      </div>
                    ) : (
                      <>
                        <div className={`font-bold ${groupSelected === 'starter' ? 'text-white' : 'text-gray-300'}`}>
                          HKD {formatPrice(g.starter.price)}
                        </div>
                      </>
                    )}
                  </div>
                </label>

              </div>

              {/* Group Features */}
              <div className="mt-3 pt-3 border-t border-gray-800 text-xs text-gray-400 space-y-1">
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Small class sizes (max 4 students)
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Practice conversation with peers
                </div>
              </div>
            </div>

            <button
              onClick={() => handleGroupClassSelect(groupClassTypeId)}
              className="bg-purple-600 hover:bg-purple-700 w-full transition-colors rounded-lg py-3 px-4 font-semibold shadow-lg shadow-purple-900/50"
            >
              Join a Group Class →
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="max-w-5xl w-full">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold mb-4">Ready to learn Chinese?</h1>
            <div className="w-16 h-1 bg-purple-500 mx-auto mb-6"></div>
            <p className="text-gray-300 text-lg">
              Choose which Chinese language and how you'd like to learn it.
            </p>
          </div>

          {/* Mobile: Tabbed Interface */}
          {renderMobileTabbedContent()}

          {/* Desktop: Language selection cards (unchanged) */}
          <div className="hidden md:grid md:grid-cols-2 gap-8 mb-12">
            {renderLanguageCard(
              'mandarin',
              expandedCard === 'mandarin',
              selectedPrivateMandarin,
              setSelectedPrivateMandarin,
              selectedGroupMandarin,
              setSelectedGroupMandarin
            )}
            {renderLanguageCard(
              'cantonese',
              expandedCard === 'cantonese',
              selectedPrivateCantonese,
              setSelectedPrivateCantonese,
              selectedGroupCantonese,
              setSelectedGroupCantonese
            )}
          </div>

          {/* Contact CTAs - Desktop only */}
          <div className="text-center mt-12 hidden md:block">
            <p className="text-gray-400 mb-4">Have questions? We're here to help</p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
              {/* WhatsApp Link */}
              <a
                href="https://wa.me/85251776613"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                <span>+852-5177-6613</span>
              </a>

              {/* Separator */}
              <span className="text-gray-600 hidden md:inline">•</span>

              {/* Email Link */}
              <a
                href="mailto:aileen@mandarintutorhk.com"
                className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span>aileen@mandarintutorhk.com</span>
              </a>
            </div>

            <p className="mt-5 text-sm text-gray-400">
              Suite 316 3/F, Dah Sing Life Building 99-105 Des Voeux Road Central, Hong Kong
            </p>
          </div>

          {/* Mobile spacing for sticky bar */}
          <div className="h-32 md:hidden" aria-hidden="true"></div>
        </div>
      </div>

      {/* Sticky Mobile Contact Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 shadow-lg z-50">
        {/* Questions header */}
        <div className="text-center pt-2 pb-1">
          <p className="text-gray-400 text-xs">Questions?</p>
        </div>
        
        <div className="px-4 pb-3 flex items-center justify-center gap-3">
          <a
            href="https://wa.me/85251776613"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex-1"
          >
            <svg className="w-5 h-5 flex-shrink-0 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            <span className="text-sm font-semibold text-white">WhatsApp</span>
          </a>
          
          <a
            href="mailto:aileen@mandarintutorhk.com"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex-1"
          >
            <svg className="w-5 h-5 flex-shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-semibold text-white">Email</span>
          </a>
        </div>

        <div className="px-4 pb-3 text-center">
          <p className="text-xs text-gray-400">
            Suite 316 3/F, Dah Sing Life Building 99-105 Des Voeux Road Central, Hong Kong
          </p>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelectionPage;